import React, { useEffect, useState } from "react";
import ReactTable from "react-table-v6";
import commaNumber from "comma-number";
import Window from "../../../elements/window";
import Loader from "../../../../settings/loader";
import { getMenuReport } from "../api";
import "react-table-v6/react-table.css";
import "./style.scss";

const RANK_RULES = [
  {
    rank: "STAR",
    rule: "(MM% > MM Rule AND Avg Profit > PC Rule) item should be retained.",
  },
  {
    rank: "WORKHORSE",
    rule: "(MM% > MM Rule AND Avg Profit < PC Rule) item should be repriced.",
  },
  {
    rank: "DOG",
    rule: "(MM% < MM Rule AND Avg Profit < PC Rule) item should be removed.",
  },
  {
    rank: "CHALLENGE",
    rule: "(MM% < MM Rule AND Avg Profit > PC Rule) item should be repositioned on menu.",
  },
];

const ITEM_COLUMNS = [
  { key: "itemRef", label: "#", type: "text", width: 70 },
  { key: "itemDescription", label: "Item Description", type: "text", minWidth: 200 },
  { key: "quantity", label: "Quantity", totalKey: "quantity" },
  { key: "menuMix", label: "Menu Mix %" },
  { key: "avgPrice", label: "Avg. Price" },
  { key: "avgCost", label: "Avg. Cost" },
  { key: "avgProfit", label: "Avg. Profit" },
  { key: "totalSales", label: "Total Sales", totalKey: "sales" },
  { key: "totalCost", label: "Total Cost", totalKey: "cost" },
  { key: "totalProfit", label: "Total Profit", totalKey: "profit" },
  { key: "profitMargin", label: "Profit Margin" },
  { key: "rank", label: "Rank", type: "rank", width: 120 },
];

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? commaNumber(number.toFixed(2)) : "";
};

const formatCell = (column, value) =>
  column.type ? value ?? "" : formatNumber(value);

const getOutletName = (outlet, filterData) => {
  const chain = filterData.chains.find((cha) => cha.chaKey === outlet.chaKey);
  const brand = filterData.brands.find((bra) => bra.braKey === outlet.braKey);
  const chainName = chain?.name || outlet.chaKey || "";
  return brand?.name ? `${brand.name} - ${chainName}` : chainName;
};

const hasRankCategories = (items) =>
  items.some((item) => RANK_RULES.some((rule) => rule.rank === item.rank));

// Excel sheet names: max 31 chars, unique, no []:*?/\
const toSheetName = (name, index, used) => {
  let sheet = (name || `Outlet ${index + 1}`).replace(/[[\]:*?/\\]/g, " ");
  sheet = sheet.slice(0, 31);
  let unique = sheet;
  let counter = 1;
  while (used.has(unique)) {
    const suffix = ` (${counter++})`;
    unique = sheet.slice(0, 31 - suffix.length) + suffix;
  }
  used.add(unique);
  return unique;
};

function MenuReportView(props) {
  const [state, setState] = useState({ load: false, outlets: [], error: null });

  useEffect(() => {
    if (!props.user?.data || !props.filter) return;
    let active = true;
    const { data } = props.filter;

    getMenuReport({
      apiBaseUrl: props.chimeraserv.urlGet,
      usrKey: props.user.data.usrKey,
      reportType: props.reportType,
      chaKeys: data.chains.map((chain) => chain.chaKey),
      startDate: new Date(data.date.start * 1000),
      endDate: new Date(data.date.end * 1000),
      revCent: (data.revCent || []).map((rc) => rc.name),
    })
      .then((outlets) => {
        if (!active) return;
        const list = (Array.isArray(outlets) ? outlets : []).map((outlet) => ({
          ...outlet,
          name: getOutletName(outlet, data),
          items: Array.isArray(outlet.items) ? outlet.items : [],
        }));
        setState({ load: true, outlets: list, error: null });
      })
      .catch((error) => {
        if (active) setState({ load: true, outlets: [], error: error.message });
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [props.filter, props.reportType, props.user?.data]);

  return (
    <section>
      {!state.load ? (
        <Loader />
      ) : (
        <Window
          user={props.user}
          chimeraserv={props.chimeraserv}
          title={props.title}
        >
          <MenuReportBody
            title={props.title}
            filter={props.filter}
            outlets={state.outlets}
            error={state.error}
          />
        </Window>
      )}
    </section>
  );
}

function MenuReportBody(props) {
  useEffect(() => {
    if (!props.export || !props.getData || !props.outlets.length) return;
    const { data } = props.filter;
    const usedSheetNames = new Set();
    const blank = ITEM_COLUMNS.map(() => "");

    props.getData({
      type: "table",
      title: props.title,
      filter: {
        brands: data.brands.map((brand) => brand.name),
        chains: data.chains.map((chain) => chain.name),
        revCent: (data.revCent || []).map((rc) => rc.name),
        date: { start: data.date.start, end: data.date.end },
      },
      body: props.outlets.map((outlet, index) => {
        const totals = outlet.totals || {};
        const rows = outlet.items.map((item) =>
          ITEM_COLUMNS.map((column) => formatCell(column, item[column.key]))
        );
        const totalRow = ITEM_COLUMNS.map((column, colIndex) =>
          colIndex === 1
            ? "Total"
            : column.totalKey
            ? formatNumber(totals[column.totalKey])
            : ""
        );
        const ruleRows = [];
        if (outlet.rules) {
          ruleRows.push(
            blank,
            [
              "",
              "Menu Mix Rule% ((1/#items)*70%)",
              formatNumber(outlet.rules.menuMix),
              ...blank.slice(3),
            ],
            [
              "",
              "Profit Contribution Rule (Total Profit / #Items sold)",
              formatNumber(outlet.rules.profitContribution),
              ...blank.slice(3),
            ]
          );
          if (hasRankCategories(outlet.items)) {
            RANK_RULES.forEach(({ rank, rule }) =>
              ruleRows.push(["", rank, rule, ...blank.slice(3)])
            );
          }
        }
        return {
          title: toSheetName(outlet.name, index, usedSheetNames),
          head: [ITEM_COLUMNS.map((column) => column.label)],
          body: [
            ["Outlet:", outlet.name, ...blank.slice(2)],
            ...rows,
            totalRow,
            ...ruleRows,
          ],
        };
      }),
    });
    // eslint-disable-next-line
  }, [props.export]);

  if (props.error) {
    return <div className="menuReportMessage error">{props.error}</div>;
  }
  if (!props.outlets.length) {
    return (
      <div className="menuReportMessage">
        No data found for the selected filters
      </div>
    );
  }

  return (
    <div className="menuReport">
      {props.outlets.map((outlet, index) => (
        <OutletReport
          outlet={outlet}
          key={`${outlet.chaKey || "outlet"}-${index}`}
        />
      ))}
    </div>
  );
}

function OutletReport({ outlet }) {
  const totals = outlet.totals || {};
  const rules = outlet.rules;

  const columns = ITEM_COLUMNS.map((column) => ({
    Header: column.label,
    accessor: column.key,
    ...(column.width && { width: column.width }),
    ...(column.minWidth && { minWidth: column.minWidth }),
    className: column.type === "text" ? "alignLeft" : "alignRight",
    headerClassName: column.type === "text" ? "alignLeft" : "alignRight",
    Cell: (row) =>
      column.type === "rank" ? (
        <span className={`rankBadge ${String(row.value || "").toLowerCase()}`}>
          {row.value}
        </span>
      ) : (
        formatCell(column, row.value)
      ),
    filterMethod: (filter, row) =>
      String(row[filter.id] ?? "")
        .toLowerCase()
        .includes(String(filter.value).toLowerCase()),
    ...(column.type === "rank" && {
      filterMethod: (filter, row) =>
        filter.value === "all" || row[filter.id] === filter.value,
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">All</option>
          {[...new Set(outlet.items.map((item) => item.rank))]
            .filter(Boolean)
            .map((rank) => (
              <option value={rank} key={rank}>
                {rank}
              </option>
            ))}
        </select>
      ),
    }),
    Footer:
      column.key === "itemDescription" ? (
        <strong>Total</strong>
      ) : column.totalKey ? (
        <strong>{formatNumber(totals[column.totalKey])}</strong>
      ) : null,
  }));

  return (
    <div className="outletReport">
      <h3 className="outletName">{outlet.name}</h3>
      <ReactTable
        className="-striped -highlight"
        data={outlet.items}
        columns={columns}
        sortable
        filterable
        defaultPageSize={Math.min(Math.max(outlet.items.length, 5), 20)}
        noDataText="No items found"
      />

      {rules && (
        <div className="menuRules">
          <table>
            <tbody>
              <tr>
                <td>Menu Mix Rule% ((1/#items)*70%):</td>
                <td>{formatNumber(rules.menuMix)}</td>
              </tr>
              <tr>
                <td>Profit Contribution Rule (Total Profit / #Items sold):</td>
                <td>{formatNumber(rules.profitContribution)}</td>
              </tr>
              {rules.menuItemCount != null && (
                <tr>
                  <td>Menu Item Count:</td>
                  <td>{commaNumber(rules.menuItemCount)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {hasRankCategories(outlet.items) && (
            <div className="rankLegend">
              <div className="legendTitle">Rank:</div>
              {RANK_RULES.map(({ rank, rule }) => (
                <div className="legendRow" key={rank}>
                  <span className={`rankBadge ${rank.toLowerCase()}`}>
                    {rank}
                  </span>
                  <span>{rule}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MenuReportView;
